/**
 * ATL-2004: what a screen tells the person when a guarded save's outcome is unknown.
 *
 * `commitWithRedeliveryGuard` throws `WriteOutcomeUnknownError` when Firestore refused a re-delivery
 * and the follow-up server read could not settle whether the first delivery landed. The typical case:
 * the ack was lost, the network then dropped, and the probe could not reach the server. The record has
 * then almost certainly been saved. A generic «Ошибка» invites the cashier to ring the sale again,
 * which turns a prevented double increment into a real double sale. Every screen that calls a guarded
 * writer therefore states the uncertainty and points at the list to check first.
 *
 * A leaf module (no Firebase, no React Native): screens and importers import it without starting
 * Firebase, and it is tested on its own. The error is recognised by its `code`, not `instanceof`, so a
 * second copy of the class (bundling, test module registries) still matches.
 */

export const WRITE_OUTCOME_UNKNOWN_CODE = 'write-outcome-unknown'

/** Is this the guard's "the save may or may not have landed" error? */
export function isWriteOutcomeUnknown(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: unknown }).code === WRITE_OUTCOME_UNKNOWN_CODE
  )
}

/** `sale` names the sales list; `record` is every other operation (its own list). */
export type WriteOutcomeSubject = 'sale' | 'record'

/** The translation key for the unknown-outcome message about `subject`. */
export function writeOutcomeUnknownKey(subject: WriteOutcomeSubject): string {
  return subject === 'sale' ? 'writeOutcomeUnknown.sale' : 'writeOutcomeUnknown.record'
}

/**
 * The message a save screen shows for `error`: the unknown-outcome message when the guard could not
 * establish the outcome, otherwise `fallback` (the screen's existing message, unchanged).
 */
export function saveFailureText(
  error: unknown,
  t: (key: string) => string,
  fallback: string,
  subject: WriteOutcomeSubject = 'record',
): string {
  return isWriteOutcomeUnknown(error) ? t(writeOutcomeUnknownKey(subject)) : fallback
}

/**
 * Bulk importers report failures as raw text, and their report tells the user to re-import the failed
 * rows. For an unknown outcome that advice would duplicate the record, so the line says so explicitly.
 */
export const IMPORT_OUTCOME_UNKNOWN_PREFIX = 'WRITE_OUTCOME_UNKNOWN'
export function importFailureMessage(error: unknown, otherwise: string): string {
  return isWriteOutcomeUnknown(error)
    ? `${IMPORT_OUTCOME_UNKNOWN_PREFIX}: the save could not be confirmed and may already exist; check the list before importing these rows again`
    : otherwise
}
